import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';

import { flattenedDataToEnrichedCanonical } from 'services/datachecks/flattenedDataToEnrichedCanonical';
import { logError } from 'services/logging/logging';
import { useReprocessMessage } from 'services/repositories/messages/messages';
import {
  RelationshipDocumentCreationSuccessResponse,
  useRelationshipDocumentCreation,
} from 'services/repositories/relationships/relationships';
import { logSentWithHiddenErrors } from 'services/webediErrors/hiddenErrorsSubmission';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { GenericError, isDuplicatedDocumentError } from 'support/helpers/errors/errors';
import { DocumentType, MessageV2DTO, ProcessSpecificationDTO, RelationshipV2 } from 'support/types';

import { usePrecomputedEnrichedOriginal } from './helpers';

type UseEditAndReprocessParams = {
  message: MessageV2DTO | undefined;
  processSpecificationFields: ProcessSpecificationDTO['fields'] | undefined;
  relationship: RelationshipV2 | undefined;
  documentType: DocumentType | undefined;
  onSuccess?: (response: RelationshipDocumentCreationSuccessResponse) => void;
};

export function useEditAndReprocess({
  message,
  processSpecificationFields,
  relationship,
  documentType,
  onSuccess,
}: UseEditAndReprocessParams) {
  const { t } = useTranslation();
  const { mutate: createDocument, isSuccess: isCreateSuccess, error: createError } = useRelationshipDocumentCreation();
  const { mutate: reprocessAsIs, isSuccess: isReprocessAsIsSuccess, error: reprocessAsIsError } = useReprocessMessage();
  const [isReprocessing, setIsReprocessing] = useState(false);
  const resolveEnrichedOriginal = usePrecomputedEnrichedOriginal(message, processSpecificationFields, documentType);

  const reprocessMessage = useCallback(
    async (formValues: Record<string, unknown>) => {
      if (isReprocessing) {
        logError('Already reprocessing message');
        return;
      }
      if (!message) {
        logError('No message found to reprocess');
        return;
      }
      if (!processSpecificationFields) {
        logError('No process specification found for reprocess');
        return;
      }
      if (!relationship) {
        logError('No target relationship found for reprocess');
        return;
      }
      if (!documentType) {
        logError('No document type resolved for reprocess');
        return;
      }

      setIsReprocessing(true);

      const notifyBackendFailure = () => {
        const documentTypeTranslation = t(`common:messageTypes.plural.${documentType}`);
        addNotification(
          {
            title: t('webedi:notifications.createDocumentBackendFailed.title', {
              documentType: documentTypeTranslation,
            }),
            subtitle: t('webedi:notifications.createDocumentBackendFailed.subtitle', {
              documentType: documentTypeTranslation,
            }),
          },
          NotificationType.error,
        );
      };

      try {
        const enrichedCanonical = await flattenedDataToEnrichedCanonical(
          processSpecificationFields,
          documentType,
          formValues,
        );
        const enrichedOriginal = await resolveEnrichedOriginal();

        const hasNoChanges = isEqual(enrichedCanonical, enrichedOriginal);

        if (hasNoChanges && message.can_resend) {
          reprocessAsIs(
            { messageId: message.id },
            {
              onSuccess: (response) => {
                logSentWithHiddenErrors({ id: response.transactionId, type: response.transactionType });
                onSuccess?.(response);
              },
              onError: (err) => {
                logError(new GenericError('[Reprocess] Failed to reprocess message: ', { errorMessage: err.message }));
                notifyBackendFailure();
              },
              onSettled: () => {
                setIsReprocessing(false);
              },
            },
          );
          return;
        }

        createDocument(
          {
            relationshipId: relationship.id,
            data: enrichedCanonical,
            application: 'PORTAL_WEB_EDI',
            // Inherit the original message's TEST/LIVE flow classification instead of re-deriving it.
            originalMessageId: message.id,
          },
          {
            onSuccess: (response) => {
              logSentWithHiddenErrors({ id: response.transactionId, type: response.transactionType });
              onSuccess?.(response);
            },
            onError: (err) => {
              const documentTypeTranslation = t(`common:messageTypes.plural.${documentType}`);
              if (isDuplicatedDocumentError(err)) {
                addNotification(
                  {
                    title: t('webedi:notifications.duplicatedDocument.title', {
                      documentType: documentTypeTranslation,
                    }),
                    subtitle: t('webedi:notifications.duplicatedDocument.subtitle', {
                      documentType: documentTypeTranslation,
                    }),
                  },
                  NotificationType.error,
                );
                return;
              }
              logError(new GenericError('[Reprocess] Failed to create document: ', { errorMessage: err.message }));
              notifyBackendFailure();
            },
            onSettled: () => {
              setIsReprocessing(false);
            },
          },
        );
      } catch (err) {
        logError(
          new GenericError('[Reprocess] Failed to prepare document for reprocess: ', { errorMessage: String(err) }),
        );
        notifyBackendFailure();
        setIsReprocessing(false);
      }
    },
    [
      isReprocessing,
      message,
      processSpecificationFields,
      relationship,
      documentType,
      createDocument,
      reprocessAsIs,
      resolveEnrichedOriginal,
      onSuccess,
      t,
    ],
  );

  return {
    reprocessMessage,
    isReprocessing,
    isSuccess: isCreateSuccess || isReprocessAsIsSuccess,
    error: createError ?? reprocessAsIsError,
  };
}
